<template>
  <v-btn
    ref="button"
    v-bind="{ ...staticButtonProps, ...buttonSizeProps, ...buttonTypeProps, ...$attrs }"
    class="base-button--main"
    :class="buttonClasses"
    v-on="$listeners"
  >
    <!-- PREPEND ICON -->
    <slot name="icon" :data="{ ...staticIconProps }">
      <v-icon v-if="icon" v-bind="{ ...staticIconProps }">{{ icon }}</v-icon>
    </slot>

    <!-- DEFAULT TEXT SLOT -->
    <span v-if="!!$slots['default']" class="tw:normal-case tw:tracking-normal tw:leading-[1] tw:relative">
      <slot name="default"></slot>
    </span>

    <!-- APPEND ICON -->
    <slot name="appendIcon" :data="{ ...staticIconProps }">
      <v-icon v-if="appendIcon" v-bind="{ ...staticIconProps }">{{ appendIcon }}</v-icon>
    </slot>

    <!-- TOOLTIP -->
    <span
      v-if="tooltip !== undefined"
      :disabled="disabled && !!tooltip"
      ref="tooltip_wrapper"
      :class="{ 'tw:w-full tw:h-full tw:pointer-events-auto': !!tooltip }"
      class="tw:absolute tw:top-0 tw:left-0"
    >
      <v-tooltip
        v-if="tooltipMount"
        :activator="tooltipMount"
        color="font"
        :bottom="!['top', 'left', 'right'].some(k => !!tooltipProps[k])"
        v-bind="tooltipProps"
      >
        <span class="tooltip_multiline">{{ tooltip }}</span>
      </v-tooltip>
    </span>
  </v-btn>
</template>

<script>
export default {
  name: 'BaseButton',
  data: () => ({
    tooltipMount: null,
  }),
  props: {
    type: {
      type: String,
      validator: type => ['primary', 'alternative', 'text', 'map-control'].includes(type),
      default: 'primary',
    },
    size: {
      type: String,
      validator: size => ['xs', 'sm', 'base', 'lg', 'xl'].includes(size),
      default: 'base',
    },
    icon: {
      type: String,
      required: false,
    },
    appendIcon: {
      type: String,
      required: false,
    },
    color: {
      type: String,
      required: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },

    /**
     * This button component don't watch anything. If you want to display tooltip conditionally,
     * set tooltip to 'null' to trigger tooltip's wrapper render.
     * 'undefined' means nothing related to tooltip will be rendered at all
     * */
    tooltip: {
      type: String,
      required: false,
      default: undefined,
    },
    tooltipProps: {
      type: Object,
      required: false,
      default: () => ({}),
    },

    /** This prop can be used ONLY with `toggle` and `map-control` type */
    toggled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    buttonClasses() {
      let stack = [];
      if (this.isIconStandalone) stack.push('base-button--icon-standalone');
      if (this.buttonTypeProps?.class) stack.push(this.buttonTypeProps.class);
      return stack.join(' ');
    },
    isIconStandalone() {
      return !!this.icon && !this.$slots.default;
    },
    buttonSizeProps() {
      switch (this.size) {
        case 'xl':
          return { xLarge: true };
        case 'lg':
          return { large: true };
        case 'sm':
          return { small: true };
        case 'xs':
          return { xSmall: true };
        default:
          return undefined;
      }
    },
    iconSize() {
      switch (this.size) {
        case 'xl':
          return 35;
        case 'lg':
          return 27;
        case 'sm':
          return 17;
        case 'xs':
          return 13;
        default:
          return 21;
      }
    },
    staticIconProps() {
      return {
        size: this.iconSize,
      };
    },
    staticButtonProps() {
      return {
        ripple: false,
        disabled: this.disabled,
        loading: this.loading,
      };
    },
    buttonColor() {
      return this.buttonTypeProps.color;
    },
    buttonTypeProps() {
      switch (this.type) {
        case 'primary': {
          return {
            color: 'primary',
            class: 'base-button--primary',
          };
        }
        case 'alternative':
        case 'map-control': {
          const props =
            this.toggled && !this.disabled
              ? {
                  color: 'primary',
                  text: true,
                  class: 'base-button--toggled',
                }
              : {
                  color: '#fff',
                  class: 'base-button--alternative',
                };
          return this.type === 'map-control'
            ? {
                ...props,
                class: `${props.class} base-button--map-control`,
              }
            : props;
        }
        case 'text': {
          return {
            color: this.color ?? this.$_colors.iconIdle,
            text: true,
            class: 'base-button--text',
          };
        }
        default: {
          return {};
        }
      }
    },
  },
  mounted() {
    if (this.tooltip !== undefined) this.$nextTick(() => (this.tooltipMount = this.$refs.tooltip_wrapper));
  },
};
</script>

<style scoped>
@reference '@/tailwind.css';

/* GLOBAL STYLE FOR ALL BUTTONS */
.base-button--main :deep(.v-btn__content) {
  position: initial !important;
}
.base-button--main {
  @apply tw:relative!;
}
.base-button--main::before {
  @apply tw:transition-[background-color,_opacity] tw:duration-200;
}
.base-button--main :deep(.v-btn__content:has(.v-icon)) {
  @apply tw:gap-1.5;
}
.base-button--main:is(.v-btn--icon) {
  @apply tw:text-[unset];
}

/* STANDALONE ICON STYLE */
.base-button--icon-standalone {
  @apply tw:rounded-full! tw:p-0! tw:min-w-0! tw:aspect-square! tw:max-w-fit!;
}

/* TOGGLE TYPE - TOGGLED STATE */
.base-button--toggled {
  @apply tw:border! tw:border-[color-mix(in_srgb,_var(--tw-color-primary),_#fff_70%)]! tw:transition-colors tw:duration-200;
}
.base-button--toggled:hover {
  @apply tw:bg-[color-mix(in_srgb,_var(--tw-color-primary),_#fff_90%)]!;
}
.base-button--toggled:focus {
  @apply tw:bg-[color-mix(in_srgb,_var(--tw-color-primary),_#fff_83%)]!;
}
.base-button--toggled {
  @apply tw:bg-[color-mix(in_srgb,_var(--tw-color-primary),_#fff_93%)]!;
}
.base-button--toggled::before {
  @apply tw:hidden!;
}

/* ALTERNATIVE TYPE */
.base-button--alternative {
  @apply tw:border! tw:border-gray-200! tw:text-iconIdle tw:transition-colors tw:duration-200;
}
.base-button--alternative:hover {
  @apply tw:bg-gray-50!;
}
.base-button--alternative:focus {
  @apply tw:bg-gray-100!;
}
.base-button--alternative {
  @apply tw:bg-white;
}
.base-button--alternative::before {
  @apply tw:hidden!;
}

/* MAP CONTROL TYPE */
.base-button--map-control {
  @apply tw:rounded-base! tw:shadow-md!;
}
</style>
